/* eslint-disable react-hooks/set-state-in-effect */
import { LoadingButton } from "@mui/lab";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Autocomplete,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
  Grid,
  Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useDispatch } from "react-redux";

import { addPerson, usePickList, useCompanies } from "@/apis/emailMashup";
import { updatePeopleList } from "@/apis/peopleList";
import { resetDataFetched } from "@/store/slices/peopleList";
import { fetchPeopleListData } from "@/store/thunks/peopleListThunks";
import EmailUtils from "@/utils/EmailUtils";

import Flag from "../../common/flag.tsx";

import type { AppDispatch } from "@/store/store";
import type { Person } from "@/utils/types/email";
import type { People } from "@/utils/types/peopleList.ts";

interface AddPersonDialogProps {
  open: boolean;
  onClose: () => void;
  email: string;
  name: string;
  phone?: string;
  personPositionId?: string | null;
  personCompanyId?: string | null;
  personComments?: string;
  id?: string;
  edit?: boolean;
  onCreated?: (person?: Person) => void;
}

type CompanyOption = {
  company_id: string;
  value: string;
  label: string;
  name: string;
  type: string;
  flag: boolean | null;
  flagComments: string | null;
};

export const AddPersonDialog: React.FC<AddPersonDialogProps> = ({
  open,
  onClose,
  email,
  name,
  phone: initialPhone = "",
  personPositionId,
  personCompanyId,
  personComments,
  id,
  edit = false,
  onCreated,
}) => {
  const queryClient = useQueryClient();
  const [personName, setPersonName] = useState(name);
  const [personEmail, setPersonEmail] = useState(email);
  const [phone, setPhone] = useState("");
  const [title, setTitle] = useState("");
  const [positionId, setPositionId] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [comments, setComments] = useState("");
  const [clientError, setClientError] = useState<string | null>(null);
  const [severity, setSeverity] = useState<"success" | "error">("error");

  // Fetch data for dropdowns
  const { data: picklist } = usePickList();
  const { data: companies } = useCompanies();

  const positionOptions = useMemo(
    () =>
      picklist?.filter(
        (item) => item.parent_type === "person" && item.type === "position"
      ) || [],
    [picklist]
  );
  const companyOptions = useMemo<CompanyOption[]>(
    () =>
      companies
        ? companies
            .map(({ company_id, name, type, flag, flag_comments }) => {
              const normalizedType =
                typeof type === "string" ? type : type?.value || "Unknown";
              return {
                company_id,
                value: `${name} (${normalizedType})`,
                label: `${name} (${normalizedType})`,
                name,
                type: normalizedType,
                flag: flag ?? null,
                flagComments: flag_comments ?? null,
              };
            })
            .sort((a, b) => {
              const textA = (a?.name || "").toLowerCase();
              const textB = (b?.name || "").toLowerCase();
              return textA.localeCompare(textB);
            })
        : [],
    [companies]
  );
  const dispatch = useDispatch<AppDispatch>();

  const selectedCompanyOption = useMemo(() => {
    if (!companyId || !companyOptions?.length) return null;
    return companyOptions.find((opt) => opt.company_id === companyId) || null;
  }, [companyId, companyOptions]);

  useEffect(() => {
    if (open) {
      if (edit) {
        setPersonEmail(email);
        setPersonName(name);
        setPhone(initialPhone);
        setPositionId(personPositionId || null);
        setCompanyId(personCompanyId || null);
        setComments(personComments || "");
        return;
      }
      setPersonName(name);
      setPersonEmail(email);
      setPhone(initialPhone);
      setTitle("");
      setPositionId(personPositionId || null);
      setCompanyId(personCompanyId || null);
      setComments("");
    }
  }, [
    open,
    name,
    initialPhone,
    email,
    edit,
    personPositionId,
    personCompanyId,
    personComments,
  ]);

  const { mutate: createPerson, isPending } = useMutation({
    mutationFn: addPerson,
    onSuccess: (createdPerson) => {
      queryClient.invalidateQueries({ queryKey: ["people"] });
      dispatch(resetDataFetched());
      dispatch(fetchPeopleListData());
      if (onCreated) onCreated(createdPerson);
      onClose();
    },
    onError: (error) => console.error("Failed to add person:", error),
  });

  const { mutate: editPerson, isPending: isUpdating } = useMutation({
    mutationFn: (payload: any) => updatePeopleList(payload.id, payload.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["people"] });
      dispatch(resetDataFetched());
      dispatch(fetchPeopleListData());
      if (onCreated) onCreated();
      onClose();
    },
    onError: (error) => console.error("Failed to update person:", error),
  });

  const handleSnackBarClose = useCallback(() => {
    setClientError(null);
    setSeverity("error");
  }, []);

  const isEmailValid = (emailValue: string) => {
    if (emailValue) {
      if (emailValue && !EmailUtils.isValidEmail(emailValue)) {
        setSeverity("error");
        setClientError(
          "Invalid email format. Please enter a valid email address."
        );
        return false;
      }
      setClientError(null);
      return true;
    }
  };
  const handleSubmit = () => {
    if (!personName) return;
    const trimmedEmail = personEmail.trim();
    if (trimmedEmail.length) {
      const result = isEmailValid(trimmedEmail);
      if (!result) {
        return;
      }
    }

    if (edit) {
      editPerson({
        id,
        data: {
          name: personName,
          email: trimmedEmail,
          phone,
          title,
          position_id: positionId || undefined,
          company_id: companyId || undefined,
          flag_comments: comments || "", // Assuming 'flag_comments' is the field for comments
        },
      });
    } else {
      createPerson({
        name: personName,
        email: trimmedEmail,
        phone,
        title,
        position_id: positionId || undefined,
        company_id: companyId || undefined,
        flag_comments: comments || "", // Assuming 'flag_comments' is the field for comments
      });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {edit ? "Edit Person" : "Add New Person"}
      </DialogTitle>
      <DialogContent>
        <Box
          component="form"
          sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}
        >
          <TextField
            label="Name"
            value={personName}
            onChange={(e) => setPersonName(e.target.value)}
            required
            fullWidth
            autoFocus
          />
          <TextField
            label="Email"
            onChange={(e) => setPersonEmail(e.target.value)}
            value={personEmail}
            fullWidth
          />
          <TextField
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
          />
          <TextField
            label="Phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            fullWidth
          />

          <FormControl fullWidth>
            <InputLabel id="position-select-label">Position</InputLabel>
            <Select
              labelId="position-select-label"
              label="Position"
              value={positionId || ""}
              onChange={(e) => setPositionId(e.target.value as string)}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {positionOptions.map((opt) => (
                <MenuItem key={opt.id} value={opt.id}>
                  {opt.value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Autocomplete<CompanyOption, false, false, false>
            options={companyOptions || []}
            getOptionLabel={(option) => option.name || ""}
            openOnFocus
            autoHighlight
            autoComplete
            getOptionKey={(option) => option.company_id}
            value={selectedCompanyOption}
            filterOptions={(optionsFilter, state) =>
              optionsFilter.filter((option) =>
                option?.name?.toLowerCase().includes(state.inputValue?.toLowerCase())
              )
            }
            renderOption={(props, option) => (
              <li {...props}>
                <Grid container spacing={2} sx={{ width: "100%" }}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Name
                    </Typography>
                    <Flag
                      flag={option.flag}
                      flagTooltip={option.flagComments}
                      name={option.name}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" align="right" color="text.secondary">
                      Type
                    </Typography>
                    <Typography variant="body2" noWrap align="right">
                      {option.type || "Unknown"}
                    </Typography>
                  </Grid>
                </Grid>
              </li>
            )}
            ListboxProps={{
              style: { maxHeight: 350 },
            }}
            slotProps={{
              popper: {
                sx: {
                  minWidth: "fit-content",
                  maxWidth: "400px",
                  "& .MuiPaper-root": {
                    width: "auto !important",
                    boxShadow: "0px 5px 15px rgba(0, 0, 0, 0.15)",
                    border: "1px solid rgba(0, 0, 0, 0.08)",
                  },
                },
              },
            }}
            onChange={(_event, newValue) => {
              setCompanyId(newValue?.company_id || null);
            }}
            renderInput={(params) => <TextField {...params} label="Company" />}
          />

          <TextField
            label="Comments"
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            fullWidth
            multiline
            rows={3}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <LoadingButton
          onClick={handleSubmit}
          variant="contained"
          loading={isPending || isUpdating}
        >
          {edit ? "Update Person" : " Add Person"}
        </LoadingButton>
      </DialogActions>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={!!clientError}
        onClose={handleSnackBarClose}
        autoHideDuration={5000}
      >
        <Alert severity={severity}>{clientError} </Alert>
      </Snackbar>
    </Dialog>
  );
};
